import React, { useContext, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { useSeasonLeaderboardWebSocket } from '../../hooks/useSeasonLeaderboardWebSocket';
import { TeamDataContext } from '../../context/TeamData.context';
import { CurrentRaceEventContext } from '../../context/currentRaceEvent.context';
import UseCountryFlag from '../../hooks/useCountryFlag';
import './SeasonLeaderboardTable.scss';
import { SortDirection } from '../../models/tableTypes';

interface SeasonLeaderboardTableProps {
    seasonId: string;
}

type SortColumn = 'seasonPoints' | 'seasonRank' | `regatta-${number}`;

const CountryFlagWrapper: React.FC<{ country: string }> = ({ country }) => {
    return UseCountryFlag(country, { width: '24px', height: '16px' });
};

const SeasonLeaderboardTable: React.FC<SeasonLeaderboardTableProps> = ({ seasonId }) => {
    const { seasonData, connectionState } = useSeasonLeaderboardWebSocket(seasonId);
    const { getTeamByCountryCode } = useContext(TeamDataContext);
    const { allRaceEvents } = useContext(CurrentRaceEventContext);
    const [sortColumn, setSortColumn] = useState<SortColumn>('seasonRank');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    // Create a map of regatta IDs to event short names from CMS data
    const regattaNameMap = useMemo(() => {
        const map: Record<number, string> = {};
        if (allRaceEvents) {
            allRaceEvents.forEach(event => {
                const trimmedName = event.shortName.replace(/\s+Sail Grand Prix$/i, '');
                map[event.regattaId] = trimmedName;
            });
        }
        return map;
    }, [allRaceEvents]);

    // Enrich regattas with proper names from CMS
    const regattas = useMemo(() => {
        return (seasonData?.standings[0]?.regattas || []).map(regatta => ({
            ...regatta,
            regattaName: regattaNameMap[regatta.regattaId] || regatta.regattaName
        }));
    }, [seasonData?.standings[0]?.regattas, regattaNameMap]);

    const sortedStandings = useMemo(() => {
        if (!seasonData?.standings) return [];

        return [...seasonData.standings].sort((a, b) => {
            let aValue: number | null = null;
            let bValue: number | null = null;

            if (sortColumn === 'seasonPoints') {
                aValue = a.seasonPoints;
                bValue = b.seasonPoints;
            } else if (sortColumn === 'seasonRank') {
                aValue = a.seasonRank;
                bValue = b.seasonRank;
            } else if (sortColumn.startsWith('regatta-')) {
                const regattaIndex = parseInt(sortColumn.split('-')[1]);
                aValue = a.regattas[regattaIndex]?.points || null;
                bValue = b.regattas[regattaIndex]?.points || null;
            }

            if (aValue === null && bValue === null) return 0;
            if (aValue === null) return 1;
            if (bValue === null) return -1;

            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        });
    }, [seasonData, sortColumn, sortDirection]);

    if (connectionState.status === 'connecting' || connectionState.status === 'initial') {
        return (
            <div className="season-leaderboard-table-loading">
                <CircularProgress size={70} />
            </div>
        );
    }

    if (connectionState.status === 'error') {
        return (
            <div className="season-leaderboard-table-error">
                Error: {connectionState.error}
            </div>
        );
    }

    if (!seasonData) {
        return (
            <div className="season-leaderboard-table-loading">
                <CircularProgress size={70} />
            </div>
        );
    }

    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection(column === 'seasonRank' ? 'asc' : 'desc');
        }
    };

    const getSortIndicator = (column: SortColumn) => {
        if (sortColumn !== column) return '';
        return sortDirection === 'asc' ? ' ↑' : ' ↓';
    };

    return (
        <div className="season-leaderboard-table-container">
            <div className="season-leaderboard-header">
                <span className="season-leaderboard-header-text">Season {seasonId} Championship</span>
            </div>
            <div className="season-leaderboard-table-wrapper">
                <table className="season-leaderboard-table">
                    <thead>
                        <tr>
                            <th className="sticky-col country-col">Team</th>
                            {regattas.map((regatta, index) => {
                                const regattaNameWithoutYear = regatta.regattaName.replace(/\s+(20\d{2}|Season\s+\d+)$/i, '')
                                return (
                                    <th
                                        key={regatta.regattaId}
                                        className="regatta-col sortable"
                                        onClick={() => handleSort(`regatta-${index}` as SortColumn)}
                                    >
                                        {regattaNameWithoutYear}{getSortIndicator(`regatta-${index}` as SortColumn)}
                                    </th>
                                )
                            })}
                            <th
                                className="total-col sortable"
                                onClick={() => handleSort('seasonPoints')}
                            >
                                Total{getSortIndicator('seasonPoints')}
                            </th>
                            <th
                                className="position-col sortable"
                                onClick={() => handleSort('seasonRank')}
                            >
                                Rank{getSortIndicator('seasonRank')}
                            </th>
                        </tr>
                        <tr>
                            <th className="sticky-col"></th>
                            {regattas.map((regatta) => (
                                <th key={`sub-${regatta.regattaId}`} className="sub-header">Pts</th>
                            ))}
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedStandings.map((team) => {
                            const teamData = getTeamByCountryCode(team.teamAbbreviation);
                            const teamColor = teamData?.primaryTeamHexColour || '#66c1c4';

                            return (
                                <tr
                                    key={team.teamAbbreviation}
                                    style={{'--team-color': teamColor} as React.CSSProperties}
                                >
                                    <td className="sticky-col country-cell">
                                        <div className="country-info">
                                            <CountryFlagWrapper country={team.teamAbbreviation} />
                                            <span className="country-code">{team.teamAbbreviation}</span>
                                        </div>
                                    </td>
                                    {team.regattas.map((regatta) => {
                                        return (
                                            <td key={`${team.teamAbbreviation}-${regatta.regattaId}`} className="points-cell">
                                                {regatta.points !== null ? (
                                                    <>
                                                        {regatta.points}
                                                        {regatta.penaltyPoints !== 0 && (
                                                            <span className={`points-adjustment ${regatta.penaltyPoints > 0 ? 'positive' : 'negative'}`}>
                                                                ({regatta.penaltyPoints > 0 ? '+' : ''}{regatta.penaltyPoints})
                                                            </span>
                                                        )}
                                                    </>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td className="total-points-cell">
                                        <span className="points-total">{team.seasonPoints}</span>
                                        {team.pointsAdjustment !== 0 && (
                                            <span className={`points-adjustment ${team.pointsAdjustment > 0 ? 'positive' : 'negative'}`}>
                                                ({team.pointsAdjustment > 0 ? '+' : ''}{team.pointsAdjustment})
                                            </span>
                                        )}
                                    </td>
                                    <td className="final-position-cell">{team.seasonRank}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SeasonLeaderboardTable;